import type { WebTelemetry } from './web-telemetry.js';

/** The part of a router state the tracker reads. Structural, so any react-router data router fits. */
export type NavigationState = {
  navigation: { state: string };
  location: { key: string };
  matches: ReadonlyArray<{ route: { path?: string } }>;
};

export type SubscribableRouter = {
  /** The current state, used to tell a later commit from the location the router started at. */
  state?: NavigationState;
  subscribe: (listener: (state: NavigationState) => void) => () => void;
};

/**
 * Records a `navigation` span for each route transition: it starts when the router begins loading the next route and
 * ends when it settles, labelled with the matched route pattern (e.g. `/users/:id`), never the concrete URL.
 *
 * A router with no loaders or lazy routes commits a navigation without ever reporting a non-idle state, so a changed
 * location key on an idle state is recorded too, as an instant span, unless that location was already recorded by the
 * loading transition that led to it. Returns the function that stops tracking.
 */
export function trackRouterNavigations(
  router: SubscribableRouter,
  telemetry: Pick<WebTelemetry, 'startNavigation'>
): () => void {
  let current: ReturnType<WebTelemetry['startNavigation']> | undefined;
  let lastKey = router.state?.location.key;

  return router.subscribe((state) => {
    if (state.navigation.state !== 'idle') {
      current ??= telemetry.startNavigation();
      return;
    }

    const route = state.matches.at(-1)?.route.path;
    if (current) {
      current.end(route);
      current = undefined;
    } else if (lastKey !== undefined && state.location.key !== lastKey) {
      telemetry.startNavigation().end(route);
    }
    lastKey = state.location.key;
  });
}
